import { Component, OnInit } from '@angular/core';
import { ReservationQueriesService } from '../../../shared/services/reservation-queries.service';
import { Reservation } from 'src/app/shared/models/reservations.model';
import { TranslatePipe } from '../../../pipes/translate.pipe';
import { TranslationService } from 'src/app/shared/services/translation.service';
import { ClubService } from 'src/app/shared/services/club.service';
import { CdkAccordionModule } from '@angular/cdk/accordion';
import { SupabaseUserToolsService } from 'src/app/shared/services/supabase-user-tools.service';
import { UserService } from 'src/app/shared/services/user.service';
import { SupabaseService } from 'src/app/shared/services/supabase.service';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { DropdownModule } from 'primeng/dropdown';
import { FormsModule } from '@angular/forms';
import { ReservationHistoryChartComponent } from '../reservation-history-chart/reservation-history-chart.component';

@Component({
  selector: 'app-user-reservation-history',
  standalone: true,
  imports: [
    MatProgressSpinner,
    TranslatePipe,
    CdkAccordionModule,
    DropdownModule,
    FormsModule,
    ReservationHistoryChartComponent,
  ],
  templateUrl: './user-reservation-history.component.html',
  styleUrl: './user-reservation-history.component.scss',
})
export class UserReservationHistoryComponent implements OnInit {
  location: string | undefined;
  emptyReservations = true;

  loading = true;

  sortedMonths: { name: string }[] = [];
  emptyMonthMap: { [key: string]: boolean } = {};
  monthlyReservationData: { [key: string]: (Partial<Reservation> & { bookedByName: string | null })[] } = {};

  currentPageMap: { [month: string]: number } = {};
  totalReservationsMap: { [month: string]: number } = {};
  totalRecReservations: { [month: string]: number } = {};
  totalBookings: { [month: string]: number } = {};
  pageSize = 10;
  only_recurrings: boolean | undefined;

  years: number[] = [2025, 2026];
  selectedYear: number = new Date().getFullYear();
  activeView: 'chart' | 'list' = 'chart';

  /** When subscriptions flag is OFF, show verified status per month */
  showVerifiedStatus = false;
  verifiedMonthMap: { [month: string]: boolean } = {};
  /** Tracks which months have at least one reservation (loaded upfront for verified logic) */
  monthHasReservations: { [month: string]: boolean } = {};
  verifiedLoading = false;

  constructor(
    private queriesService: ReservationQueriesService,
    protected translationService: TranslationService,
    public translateService: TranslationService,
    private clubsService: ClubService,
    private userToolService: SupabaseUserToolsService,
    private userService: UserService,
    private supabaseService: SupabaseService
  ) {}

  monthNames: { [key: number]: string } = {
    1: 'January',
    2: 'February',
    3: 'March',
    4: 'April',
    5: 'May',
    6: 'June',
    7: 'July',
    8: 'August',
    9: 'September',
    10: 'October',
    11: 'November',
    12: 'December',
  };

  ngOnInit() {
    const config = this.clubsService.flags();
    this.only_recurrings = config?.only_recurring_bookings;
    this.showVerifiedStatus = !config?.subscriptions;

    if (!this.years.includes(this.selectedYear)) {
      this.selectedYear = 2026;
    }

    this.sortedMonths = Array.from({ length: 12 }, (_, i) => ({
      name: this.monthNames[i + 1],
    }));

    if (this.showVerifiedStatus) {
      this.loadVerifiedStatus();
    }
  }

  onAccordionExpanded(expanded: boolean, month: string) {
    if (expanded) {
      this.loading = true;
      this.currentPageMap[month] = this.currentPageMap[month] || 1;
      this.userReservationHistory(month);
    }
  }

  onYearChange() {
    this.emptyMonthMap = {};
    this.monthlyReservationData = {};
    this.currentPageMap = {};
    this.totalReservationsMap = {};
    this.totalRecReservations = {};
    this.totalBookings = {};
    this.verifiedMonthMap = {};
    this.monthHasReservations = {};

    if (this.showVerifiedStatus) {
      this.loadVerifiedStatus();
    }
  }

  async userReservationHistory(month: string) {
    const currentYear = this.selectedYear;
    const monthNumber = this.getMonthNumberFromName(month);

    if (!monthNumber) {
      console.warn('Invalid month name:', month);
      return;
    }

    try {
      const { data, count, error } =
        await this.queriesService.getTotalReservationsMadeFromCurrentUserByMonth(
          monthNumber,
          currentYear
        );

      if (error) {
        console.error('Error from Supabase:', error);
        this.emptyMonthMap[month] = true;
        this.loading = false;
        return;
      }

      if (!data || data.length === 0 || !count) {
        this.emptyMonthMap[month] = true;
        this.loading = false;
        return;
      }

      const userIds = [
        ...new Set(data.map((r) => r.bookedBy).filter((id): id is string => Boolean(id))),
      ];
      const idToName = await this.userToolService.getUserNames(userIds);

      const formattedData = data.map((r) => ({
        ...r,
        bookedByName: r.bookedBy ? idToName[r.bookedBy] || null : null,
      }));

      const nullRecReservations = data.filter((r) => r.bookedBy == null).length;

      this.monthlyReservationData[month] = formattedData;
      this.totalRecReservations[month] = nullRecReservations;
      this.totalReservationsMap[month] = count;
      this.totalBookings[month] = count - nullRecReservations;
      this.emptyMonthMap[month] = false;

      this.loading = false;
    } catch (err) {
      console.error('Unexpected error:', err);
      this.emptyMonthMap[month] = true;
    }
  }

  getMonthNumberFromName(name: string): number | undefined {
    return Number(
      Object.keys(this.monthNames).find(
        (key) => this.monthNames[+key].toLowerCase() === name.toLowerCase()
      )
    );
  }

  formatTime(isoString: string): string {
    const date = new Date(isoString);
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  }

  formatDateOnly(isoString: string): string {
    const date = new Date(isoString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${day}/${month}`;
  }

  /** Counts how many past/current months with reservations are verified */
  get verifiedCount(): number {
    let count = 0;
    for (let m = 1; m <= this.maxEligibleMonth; m++) {
      const name = this.monthNames[m];
      if (this.monthHasReservations[name] && this.verifiedMonthMap[name]) {
        count++;
      }
    }
    return count;
  }

  get unverifiedCount(): number {
    let count = 0;
    for (let m = 1; m <= this.maxEligibleMonth; m++) {
      const name = this.monthNames[m];
      if (this.monthHasReservations[name] && !this.verifiedMonthMap[name]) {
        count++;
      }
    }
    return count;
  }

  /** Upper month boundary (1-based) for verified/unverified logic */
  private get maxEligibleMonth(): number {
    const now = new Date();
    const currentMonth = now.getMonth() + 1;
    const currentYear = now.getFullYear();
    if (this.selectedYear < currentYear) return 12;
    if (this.selectedYear === currentYear) return currentMonth;
    return 0;
  }

  /** Whether a month is eligible AND has reservations */
  isEligibleMonth(monthName: string): boolean {
    if (!this.monthHasReservations[monthName]) return false;
    const monthNum = this.getMonthNumberFromName(monthName);
    if (!monthNum) return false;
    return monthNum <= this.maxEligibleMonth;
  }

  private async loadVerifiedStatus() {
    this.verifiedLoading = true;
    try {
      const session = await this.userService.getSession();
      const userId = session?.data?.session?.user?.id;
      if (!userId) {
        this.verifiedLoading = false;
        return;
      }

      // Fetch verified status and reservation counts in parallel
      const [completionResult, ...countResults] = await Promise.all([
        this.supabaseService.supabase
          .from('users_subscription_completion')
          .select('month, completed')
          .eq('user_id', userId)
          .eq('club_id', this.supabaseService.clubId)
          .eq('year', this.selectedYear),
        ...Array.from({ length: 12 }, (_, i) =>
          this.queriesService.getTotalReservationsMadeFromCurrentUserByMonth(
            i + 1,
            this.selectedYear
          )
        ),
      ]);

      // Build verified map
      this.verifiedMonthMap = {};
      if (!completionResult.error && completionResult.data) {
        completionResult.data.forEach((row) => {
          if (row.month) {
            this.verifiedMonthMap[row.month] = row.completed;
          }
        });
      }

      // Build month-has-reservations map
      this.monthHasReservations = {};
      for (let i = 0; i < 12; i++) {
        const monthName = this.monthNames[i + 1];
        const { count } = countResults[i];
        this.monthHasReservations[monthName] = (count ?? 0) > 0;
      }
    } catch (err) {
      console.error('Error loading verified status:', err);
    }
    this.verifiedLoading = false;
  }
}
